import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import LocalePreferences from '/@/components/locale-preferences';
import Container from '/@/components/new-login/container';
import agroceresLogo from '/@/components/new-login/login/assets/agroceres-logo.svg';
import agroesteLogo from '/@/components/new-login/login/assets/agroeste-logo.svg';
import cultivioLogo from '/@/components/new-login/login/assets/cultivio-logo.svg';
import expertobayerLogo from '/@/components/new-login/login/assets/expertobayer-logo.svg';
import latijeretaLogo from '/@/components/new-login/login/assets/latijereta-logo.svg';
import dekalbLogo from '/@/components/new-login/login/assets/logo_dekalb.svg';
import mdLogo from '/@/components/new-login/login/assets/md-logo.svg';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { setPreferences } from '/@/store/session/actions';
import { getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';
import useIsMobile from '/@/utils/hooks/is-mobile';

/**
 * The bundled per-theme logos the login form uses. Preferred over
 * `settings.app.logo`, which points at uploaded media and is not always present.
 */
const THEME_LOGOS: Record<string, string> = {
    agroceres: agroceresLogo,
    agroeste: agroesteLogo,
    cultivio: cultivioLogo,
    dekalb: dekalbLogo,
    expertobayer: expertobayerLogo,
    fieldview: dekalbLogo,
    latijereta: latijeretaLogo,
    md: mdLogo,
};

/** Brand logo for the active theme, sized to match the login form's. */
export const BrandLogo = ({ sx = {} }) => (
    <Box
        component='img'
        src={THEME_LOGOS[settings.app.theme] ?? cultivioLogo}
        alt={settings.app.name}
        sx={{ width: 280, maxWidth: '100%', ...sx }}
    />
);

/**
 * Shared layout for the panel holding an access-request screen.
 *
 * On desktop `Container` renders the artwork and this panel as flex siblings without
 * giving either a width, so the panel needs its own or a long line makes the column
 * grow past the viewport half. On mobile it is the whole screen.
 */
export const panelSx = {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: { xs: '100%', md: '50vw' },
    minHeight: { xs: '100vh', md: 'auto' },
    px: { xs: 4, md: 10.5 },
    py: 2,
    // No `overflow` of its own: on desktop `Container`'s right column already scrolls,
    // and on mobile a nested scroller would stop the document from scrolling normally.
};

/**
 * Below `md` the brand artwork is dropped and the panel takes the whole screen, the
 * same trade-off `NewLogin` makes -- it renders its forms outside `Container` on
 * mobile rather than squeezing them beside the artwork.
 */
export const AccessRequestScreen = ({ onBack, children }) => {
    const isMobile = useIsMobile();
    const country = useSelector(getSessionCountry);
    const language = useSelector(getSessionLanguage);
    const dispatch = useDispatch();
    const setLanguageAndCountry = bindActionCreators(setPreferences, dispatch);

    /**
     * Mirrors the mobile login form's header (`LoginForm.renderHeader`): back arrow on
     * the left, country picker on the right, so moving between login and these screens
     * does not change the affordance.
     *
     * The arrow shows at every width -- these are the only screens in the flow you can
     * reach directly from an email, so there is always somewhere to go back to. The
     * country picker is mobile-only, because on desktop `Container`'s left panel
     * already renders one and two would be confusing.
     */
    const header = (
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
            <IconButton aria-label={_('Volver')} onClick={onBack} sx={{ ml: -1, color: 'grey.600' }}>
                <ArrowBackIcon />
            </IconButton>
            {isMobile && (
                <LocalePreferences
                    openUp
                    appearance='row'
                    showLabels={false}
                    country={country.id}
                    language={language}
                    onChange={setLanguageAndCountry}
                    showCountry
                    handleCountryChange={() => {}}
                    showLanguage={false}
                />
            )}
        </Stack>
    );

    const panel = (
        <Box sx={panelSx}>
            {onBack && header}
            {children}
        </Box>
    );

    if (isMobile) return panel;
    return (
        <Container showLanguage hideButtons>
            {panel}
        </Container>
    );
};

/**
 * Terminal screen of the request flow: brand logo, a heading and a line of copy, the
 * support address and the way back. Only the two texts change between outcomes.
 */
const AccessRequestOutcome = ({
    title,
    description,
    onBackToStart,
}: {
    title: string;
    description: string;
    onBackToStart: () => void;
}) => (
    <>
        <BrandLogo sx={{ mb: 4 }} />
        <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 700 }}>
            {title}
        </Typography>
        <Typography variant='body1' sx={{ color: 'grey.700', mt: 1 }}>
            {description}
        </Typography>
        <Typography variant='subtitle2' color='primary' sx={{ mt: 3 }}>
            {_('Si tenés alguna duda escribinos a ')}
            <Link href={`mailto:${settings.app.infoEmail}`} color='secondary'>
                {settings.app.infoEmail}
            </Link>
        </Typography>
        <Stack direction='row' justifyContent='center' sx={{ mt: 3 }}>
            <Button
                variant='contained'
                startIcon={<ArrowBackIcon />}
                onClick={onBackToStart}
                sx={{ width: { xs: '100%', md: 'auto' } }}
            >
                {_('Volver al inicio')}
            </Button>
        </Stack>
    </>
);

/** Confirmation shown after a request is created and left pending review. */
const RequestAccessSubmitted = ({ onBackToStart }: { onBackToStart: () => void }) => (
    <AccessRequestOutcome
        title={_('Recibimos tu solicitud')}
        description={_(
            'Nuestro equipo la revisa en un plazo de hasta 72 horas y te contacta por email para confirmar tu acceso.'
        )}
        onBackToStart={onBackToStart}
    />
);

/**
 * Shown instead of an inline error when the backend rejects the submission because
 * this email already has a request waiting for review -- there is nothing for the
 * applicant to fix, so the form is replaced by the state their request is in.
 */
export const RequestAccessUnderReview = ({ onBackToStart }: { onBackToStart: () => void }) => (
    <AccessRequestOutcome
        title={_('Tu solicitud está en revisión')}
        description={_(
            'Ya recibimos tus datos y los estamos revisando. Te contactamos por email en cuanto tengamos novedades.'
        )}
        onBackToStart={onBackToStart}
    />
);

export default RequestAccessSubmitted;
